import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import OnboardingVideo from "@/components/OnboardingVideo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { COUNTRIES, getStatesFor } from "@/data/locations";
import {
  Smartphone, Monitor, Wallet, KeyRound, Network, DollarSign, Coins, Link2,
  Cpu, ArrowDownToLine, Repeat, CheckCircle2, Circle, Clock, Lock, ArrowRight,
  ExternalLink, Send, PartyPopper, BookOpen, X, Video, Globe, ClipboardCheck,
} from "lucide-react";

// The 12-step CaryPact onboarding — mirrors the illustrated manual. Keys MUST
// match ONBOARDING_STEP_KEYS in the backend.
const STEPS = [
  {
    key: "install-tp", group: "A · Set up your TP Wallet", icon: Smartphone,
    title: "Install TP Wallet",
    desc: "TokenPocket (TP Wallet) is the wallet everything runs through.",
    mobile: "Install the TokenPocket app from the App Store / Google Play.",
    web: "Add the TokenPocket extension to Chrome from tokenpocket.pro.",
    link: { href: "https://www.tokenpocket.pro/en/download/app", label: "Download TP Wallet" },
    popup: { route: "/setup", label: "Open Smart Setup" },
  },
  {
    key: "create-wallet", icon: Wallet,
    title: "Create your wallet & back up the seed phrase",
    desc: "Create a new wallet and write the 12 words on paper — store them safely offline. This is the ONLY way to recover your wallet; nobody can restore it for you.",
    mobile: "Wallet → Create → write down the 12 words in order.",
    web: "Same flow in the extension — never screenshot the words.",
    popup: { route: "/setup", label: "Open Smart Setup" },
  },
  {
    key: "add-bot-chain", icon: Network,
    title: "Add the BOT Chain network",
    desc: "Add BOT Chain as a custom network inside TP Wallet so your CaryPact assets are visible.",
    popup: { route: "/setup", label: "Open Smart Setup" },
  },
  {
    key: "gas-bnb", group: "B · Fund your wallet", icon: DollarSign,
    title: "Get a little BNB for gas",
    desc: "A few dollars of BNB covers transaction fees. Buy inside TP Wallet or send from an exchange.",
  },
  {
    key: "get-usdt", icon: DollarSign,
    title: "Get USDT into your wallet",
    desc: "Move the USDT you plan to commit into your TP Wallet (BNB Chain), then bridge to BOT Chain.",
  },
  {
    key: "add-tokens", icon: Coins,
    title: "Add USDT & CA as custom tokens on BOT Chain",
    desc: "Register both tokens on the BOT Chain network so balances show up correctly.",
  },
  {
    key: "open-invite", group: "C · Connect to CaryPact", icon: Link2,
    title: "Open your CaryPact invitation",
    desc: "Your sponsor's personal invitation opens your CaryPact backoffice. Use the button below — it carries your invitation automatically.",
    mobile: "Open the button link inside TP Wallet's DApp browser (Discover tab).",
    web: "Open the link in the browser where the TP extension is installed.",
    carypact: true,
  },
  {
    key: "enter-code", icon: KeyRound,
    title: "Register & sign in CaryPact",
    desc: "The invitation code fills in automatically from your link. Confirm, sign with your wallet, and you'll land on the Bot Chain Benchmark Protocol home screen.",
  },
  {
    key: "buy-hashrate", icon: Cpu,
    title: "Buy computing power (activate your mining)",
    desc: "Commit your USDT to hashrate — this is what starts your daily CA mining rewards.",
  },
  {
    key: "claim-rewards", group: "D · Earn, stake & cash out", icon: ArrowDownToLine,
    title: "Claim your CA mining rewards",
    desc: "Rewards accrue daily. Claim them into your wallet from the CaryPact dashboard.",
  },
  {
    key: "stake", icon: Coins,
    title: "Stake CA — flexible or fixed-term",
    desc: "Optionally stake claimed CA for extra yield. Flexible = withdraw any time; fixed = higher rate.",
  },
  {
    key: "cashout", icon: Repeat,
    title: "Cash out: CA → BOT → USDT → your bank",
    desc: "When you want cash, swap CA to USDT, bridge back to BNB Chain and off-ramp via your exchange.",
  },
];

// The 4 onboarding "captures" — country/state, own CaryPact invite link, wallet
// address (name + email were captured at enrol). All 4 in → auto-Active, no approvals.
function CaptureCard({ captures, onSaved }) {
  const [form, setForm] = useState({ country: "", state: "", carypact_link: "", wallet_address: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (captures) setForm((f) => ({
      country: f.country || captures.country || "",
      state: f.state || captures.state || "",
      carypact_link: f.carypact_link || captures.carypact_link || "",
      wallet_address: f.wallet_address || captures.wallet_address || "",
    }));
  }, [captures]); // eslint-disable-line react-hooks/exhaustive-deps

  const filled = ["country", "state", "carypact_link", "wallet_address"].filter((k) => (form[k] || "").trim()).length;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/captures", form);
      onSaved(data);
      if (data.captures_complete) {
        toast.success("That's everything — your access is unlocked and you're on the team chart!", { duration: 8000 });
      } else {
        toast.success("Saved — fill in the rest whenever you have it.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass p-5 mb-5 fade-up" data-testid="capture-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={15} style={{ color: "#00863A" }} />
          <span className="kpi-label !mb-0">Your details — unlocks your access</span>
        </div>
        <span className="text-xs font-mono font-semibold text-ink-2" data-testid="capture-count">{filled} / 4</span>
      </div>
      <p className="text-xs text-ink-3 leading-relaxed mb-4">
        Fill these in as you work through the steps below. The moment all four are in, you're{" "}
        <b>automatically activated</b> — full portal access and your spot on the team chart. No waiting for approval.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="kpi-label flex items-center gap-1.5 mb-1"><Globe size={11} /> Country</span>
          <select
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value, state: "" })}
            className="input-glass w-full text-sm"
            data-testid="capture-country"
          >
            <option value="">Select country…</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="kpi-label mb-1 block">State / Province</span>
          <select
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className="input-glass w-full text-sm"
            disabled={!form.country}
            data-testid="capture-state"
          >
            <option value="">{form.country ? "Select state…" : "Pick a country first"}</option>
            {getStatesFor(form.country).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>
      <label className="block mb-3">
        <span className="kpi-label flex items-center gap-1.5 mb-1"><Link2 size={11} /> Your CaryPact invite link</span>
        <input
          value={form.carypact_link}
          onChange={(e) => setForm({ ...form, carypact_link: e.target.value })}
          placeholder="https://app.carypact.com/?inviteCode=…"
          className="input-glass w-full text-sm font-mono"
          data-testid="capture-link"
        />
        <span className="text-[11px] text-ink-3 mt-1 block">In CaryPact: <b>User Center → Invite Friends → Copy invite link</b> — paste it here.</span>
      </label>
      <label className="block mb-4">
        <span className="kpi-label flex items-center gap-1.5 mb-1"><Wallet size={11} /> Your wallet address</span>
        <input
          value={form.wallet_address}
          onChange={(e) => setForm({ ...form, wallet_address: e.target.value })}
          placeholder="0x…"
          className="input-glass w-full text-sm font-mono"
          data-testid="capture-wallet"
        />
        <span className="text-[11px] text-ink-3 mt-1 block">In TP Wallet: tap <b>BOT → Receive → Copy</b> — paste the address here.</span>
      </label>
      <button onClick={save} disabled={saving} className="btn-primary w-full text-sm" data-testid="capture-save">
        {saving ? "Saving…" : filled === 4 ? <>Save & unlock my access <ArrowRight size={14} /></> : "Save my details"}
      </button>
    </div>
  );
}

export default function GettingStarted() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [popup, setPopup] = useState(null); // { route, label, key, title }

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch { /* toggles surface their own errors */ }
  }, []);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const locked = !!user?.onboarding_locked;
  const submitted = !!status?.submitted_at;
  const steps = status?.steps || {};
  const sponsor = status?.sponsor;
  const doneCount = STEPS.filter((s) => steps[s.key]).length;
  const allDone = doneCount === STEPS.length;

  // While locked, poll so the page unlocks the moment the admin approves.
  useEffect(() => {
    if (!locked) return;
    const t = setInterval(() => { refresh(); fetchStatus(); }, 12000);
    return () => clearInterval(t);
  }, [locked, refresh, fetchStatus]);

  const toggleStep = async (key) => {
    setBusyKey(key);
    try {
      const { data } = await api.post("/onboarding/steps", { steps: { [key]: !steps[key] } });
      setStatus((prev) => ({ ...prev, ...data, sponsor: prev?.sponsor }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your progress.");
    } finally {
      setBusyKey(null);
    }
  };

  const submitForApproval = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/onboarding/submit");
      setStatus((prev) => ({ ...prev, ...data, sponsor: prev?.sponsor }));
      toast.success("Checklist submitted — your admin has been asked to approve your access.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  // Approved / not gated → celebrate + hand over to the app.
  if (user && !locked) {
    return (
      <div className="min-h-screen pb-16">
        <Header />
        <div className="max-w-xl mx-auto px-6 pt-20">
          <div className="glass-strong p-8 text-center fade-up" data-testid="onboarding-approved-card">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(0,134,58,0.12)" }}>
              <PartyPopper size={26} style={{ color: "#00863A" }} />
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">You&rsquo;re all set.</h1>
            <p className="text-ink-2 text-sm mb-6">
              Your onboarding is approved — the full BotChainCP console is unlocked: wallets, revenue tracking, forecasts and more.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mx-auto" data-testid="enter-app-btn">
              Enter BotChainCP <ArrowRight size={14} />
            </button>
          </div>
        </div>
        <HelpFab />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-2xl mx-auto px-6 pt-10" data-testid="getting-started-page">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><Lock size={11} style={{ marginRight: 4 }} /> New member setup</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Getting started.</h1>
          <p className="text-ink-2 mt-3 text-sm leading-relaxed">
            Welcome to <b>CaryPact</b>{user?.name ? <>, <b>{user.name.split(" ")[0]}</b></> : null}. Work through the
            12 steps below and fill in your four details as you go — the moment they're all in, your access
            unlocks <b>automatically</b>. Stuck at any point? Message us from the chat bubble.
          </p>
        </div>

        {/* Watch-first explainer video */}
        <div className="mb-5 fade-up">
          <div className="flex items-center gap-2 mb-2.5">
            <Video size={15} style={{ color: "#7C3AED" }} />
            <span className="kpi-label !mb-0">Watch this first — 2-minute walkthrough</span>
          </div>
          <OnboardingVideo />
        </div>

        {/* The 4 captures — completing them auto-activates the member */}
        <CaptureCard
          captures={status?.captures}
          onSaved={async (data) => {
            setStatus((prev) => ({ ...prev, ...data }));
            if (data.captures_complete) await refresh();
          }}
        />

        {/* Progress */}
        <div className="glass p-4 mb-5 fade-up" data-testid="onboarding-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="kpi-label !mb-0">Your progress</span>
            <span className="text-xs font-mono font-semibold text-ink-2" data-testid="progress-count">{doneCount} / {STEPS.length} complete</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.07)" }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(doneCount / STEPS.length) * 100}%`, background: "linear-gradient(90deg, #4060FF, #7C3AED)" }}
              data-testid="progress-bar"
            />
          </div>
        </div>

        {/* Awaiting approval banner */}
        {submitted && locked && (
          <div
            className="rounded-2xl p-4 mb-5 flex items-start gap-3 fade-up"
            style={{ background: "rgba(64,96,255,0.08)", border: "1px solid rgba(64,96,255,0.22)" }}
            data-testid="awaiting-approval-banner"
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(64,96,255,0.14)" }}>
              <Clock size={16} style={{ color: "#4060FF" }} />
            </div>
            <div className="text-sm leading-relaxed">
              <div className="font-semibold text-ink">Checklist submitted — approval in progress.</div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-testid="approval-steps">
                <span className={`chip text-[11px] ${status?.sponsor_approved_at ? "chip-green" : "chip-amber"}`} data-testid="approval-step-sponsor">
                  1. Sponsor {status?.sponsor_approved_at ? "approved ✓" : "review…"}
                </span>
                <span className="chip chip-gray text-[11px]" data-testid="approval-step-admin">2. Admin confirmation…</span>
              </div>
              <div className="text-ink-2 text-xs mt-1.5">
                Your sponsor approves first, then an admin confirms. This page unlocks automatically the moment you're confirmed — no need to log out.
              </div>
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="space-y-3">
          {STEPS.map((s, i) => {
            const done = !!steps[s.key];
            const Icon = s.icon;
            return (
              <React.Fragment key={s.key}>
                {s.group && (
                  <div className="kpi-label !mb-0 pt-3" data-testid={`step-group-${s.key}`}>{s.group}</div>
                )}
                <div
                  className="glass p-4 flex items-start gap-4 fade-up transition-all"
                  style={{ animationDelay: `${Math.min(i * 0.04, 0.4)}s`, opacity: done ? 0.72 : 1 }}
                  data-testid={`step-row-${s.key}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: done ? "rgba(0,134,58,0.10)" : "rgba(64,96,255,0.10)" }}>
                    <Icon size={17} style={{ color: done ? "#00863A" : "#4060FF" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-sm ${done ? "line-through text-ink-3" : "text-ink"}`}>{i + 1}. {s.title}</div>
                    <div className="text-xs text-ink-2 mt-1 leading-relaxed">{s.desc}</div>

                    {(s.mobile || s.web) && (
                      <div className="mt-2 space-y-1">
                        {s.mobile && (
                          <div className="text-[11px] text-ink-2 flex items-start gap-1.5">
                            <Smartphone size={11} className="shrink-0 mt-0.5 text-ink-3" /> <span><b>Mobile:</b> {s.mobile}</span>
                          </div>
                        )}
                        {s.web && (
                          <div className="text-[11px] text-ink-2 flex items-start gap-1.5">
                            <Monitor size={11} className="shrink-0 mt-0.5 text-ink-3" /> <span><b>Web:</b> {s.web}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sponsor's CaryPact invitation — embedded in the button, never shown raw */}
                    {s.carypact && (
                      sponsor?.link ? (
                        <a
                          href={sponsor.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary !py-2 !px-4 text-xs mt-2.5 inline-flex"
                          data-testid="carypact-invite-btn"
                        >
                          <Link2 size={12} /> Open your CaryPact invitation →
                        </a>
                      ) : (
                        <div className="mt-2.5 text-[11px] font-medium" style={{ color: "#B45309" }} data-testid="carypact-invite-missing">
                          Your personal invitation isn&rsquo;t linked yet — message your admin from the chat bubble and they&rsquo;ll attach it.
                        </div>
                      )
                    )}

                    <div className="flex flex-wrap items-center gap-x-4">
                      {s.popup && (
                        <button
                          onClick={() => setPopup({ ...s.popup, key: s.key, title: s.title })}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold mt-2 hover:underline"
                          style={{ color: "#7C3AED" }}
                          data-testid={`step-popup-${s.key}`}
                        >
                          <BookOpen size={11} /> {s.popup.label}
                        </button>
                      )}
                      {s.link && (
                        <a
                          href={s.link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold mt-2 hover:underline"
                          style={{ color: "#4060FF" }}
                          data-testid={`step-link-${s.key}`}
                        >
                          <ExternalLink size={11} /> {s.link.label}
                        </a>
                      )}
                    </div>

                    {/* Video placeholder — admin drops real training videos in later */}
                    <div
                      className="mt-2.5 rounded-xl flex items-center gap-2 px-3 py-2"
                      style={{ background: "rgba(15,18,38,0.035)", border: "1px dashed rgba(15,18,38,0.16)" }}
                      data-testid={`step-video-${s.key}`}
                    >
                      <Video size={12} className="text-ink-3 shrink-0" />
                      <span className="text-[11px] text-ink-3">Training video — coming soon</span>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleStep(s.key)}
                    disabled={busyKey === s.key}
                    className="shrink-0 transition-transform hover:scale-110 disabled:opacity-50"
                    title={done ? "Mark as not done" : "Mark as done"}
                    data-testid={`step-toggle-${s.key}`}
                  >
                    {done
                      ? <CheckCircle2 size={26} style={{ color: "#00863A" }} />
                      : <Circle size={26} style={{ color: "rgba(15,18,38,0.25)" }} />}
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Submit */}
        <div className="mt-6 fade-up">
          <button
            onClick={submitForApproval}
            disabled={!allDone || submitted || submitting}
            className="btn-primary w-full justify-center disabled:opacity-50"
            data-testid="submit-approval-btn"
            title={!allDone ? "Tick every step first" : submitted ? "Already submitted — waiting for approval" : "Send for admin approval"}
          >
            <Send size={14} />
            {submitted ? "Submitted — awaiting approval" : submitting ? "Submitting…" : "I'm done — request approval"}
          </button>
          {!allDone && (
            <p className="text-[11px] text-ink-3 text-center mt-2">
              Complete all {STEPS.length} steps to request approval. Stuck? Message your admin from the chat bubble.
            </p>
          )}
        </div>
      </div>

      {/* Training popup — Smart Setup / illustrated manual, markable as done */}
      {popup && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4" data-testid="training-popup">
          <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.5)", backdropFilter: "blur(4px)" }} onClick={() => setPopup(null)} />
          <div className="relative w-full max-w-5xl h-[85vh] rounded-2xl overflow-hidden flex flex-col" style={{ background: "#F4F6FE", boxShadow: "0 30px 80px rgba(15,18,38,0.35)" }}>
            <div className="flex items-center gap-3 px-4 py-3 bg-white/95 border-b border-black/5">
              <div className="flex-1 leading-tight min-w-0">
                <div className="kpi-label !mb-0">Training</div>
                <div className="font-semibold text-sm truncate">{popup.title}</div>
              </div>
              <button
                onClick={() => { if (!steps[popup.key]) toggleStep(popup.key); setPopup(null); }}
                className="btn-primary !py-2 !px-3.5 text-xs"
                data-testid="training-popup-done"
              >
                <CheckCircle2 size={13} /> Mark step done
              </button>
              <button onClick={() => setPopup(null)} className="org-icon-btn" title="Close" data-testid="training-popup-close"><X size={15} /></button>
            </div>
            <iframe src={popup.route} title={popup.title} className="flex-1 w-full border-0" />
          </div>
        </div>
      )}

      <HelpFab />
    </div>
  );
}
